<template>
  <div class="card">
    <h4>{{ $t("app.company.history.transactionsList") }}</h4>
    <p>{{ $t("app.company.history.transactionsDesc") }}</p>
    <Table v-if="!isTransactionsPending" :data="transactions" :fields="state.fields">
      <template #creation_time="{ data }">
        <div
          :style="{ width: 'fit-content' }"
          v-text="getDate(data.creation_time, 'LL')"
          v-tooltip="{
            content: getDate(data.creation_time, 'LLLL'),
            placement: 'top',
          }"
        />
      </template>
      <template #status="statusProps">
        <div
          class="tag"
          :class="{
            green: statusProps.data.paid_time,
            red: statusProps.data.canceled_time,
            'grey-light': !statusProps.data.paid_time && !statusProps.data.canceled_time,
          }"
        >
          <span v-if="statusProps.data.paid_time">{{ $t("app.company.history.statusType.paid") }}</span>
          <span v-else-if="statusProps.data.canceled_time"> {{ $t("app.company.history.statusType.canceled") }} </span>
          <span v-else>{{ $t("app.company.history.statusType.pending") }}</span>
        </div>
      </template>
      <template #actions="actionsProps">
        <Button :label="$t('app.company.history.downloadInvoice')" class="icon icon-left outline secondary" icon="bi-upload" :disabled="!actionsProps.row.paid_time" :loading="state.btnIsLoading === actionsProps.row.transaction_id" @click="getInvoice(actionsProps.row.transaction_id)" />
      </template>
      <template #empty>
        <Empty :message="$t('app.company.history.noTransactions')" :dark="isDark" icon="folder" />
      </template>
    </Table>
  </div>
</template>

<script setup lang="ts">
import { reactive } from "vue";
import { useRoute } from "vue-router";
import { useDark } from "@vueuse/core";
import { useToast } from "vue-toastification";

import { getDate, dateIsBefore } from "@/composables/utils.js";
import Empty from "@/components/table/Empty.vue";

import { useOrganisationTransactions } from "@/queries/organisation.query";
import { organisationApi } from "@/api/organisation.api";

const isDark = useDark();
const route = useRoute();
const toast = useToast();
const { t } = useI18n();

const { data: transactions, isPending: isTransactionsPending } = useOrganisationTransactions();

const state = reactive({
  btnIsLoading: false,
  fields: [
    {
      id: "name",
      label: t("app.company.history.transactionName"),
      sort(a, b, dir) {
        if (dir === "ASC") return a.localeCompare(b);
        return b.localeCompare(a);
      },
    },
    {
      id: "amount_total",
      label: t("app.company.history.transactionPrice"),
      defaultSortDir: "DESC",
      template(value) {
        return `${value}€`;
      },
      sort(a, b, dir) {
        if (dir === "ASC") return a - b;
        return b - a;
      },
    },
    {
      id: "creation_time",
      label: t("app.company.history.transactionDate"),
      defaultSortDir: "DESC",
      sort(a, b, dir) {
        if (dir === "ASC") {
          return dateIsBefore(b, a) ? 1 : -1;
        } else {
          return dateIsBefore(a, b) ? 1 : -1;
        }
      },
    },
    {
      id: "status",
      label: t("app.company.history.status"),
      defaultSortDir: "DESC",
      multipleSort: true,
      sort(a, b, dir) {
        if (dir === "ASC") {
          if (b.paid_time && !a.paid_time) return -1;
          if (!b.paid_time && a.paid_time) return 1;

          if (b.canceled_time && !a.canceled_time) return -1;
          if (!b.canceled_time && a.canceled_time) return 1;

          return dateIsBefore(b.creation_time, a.creation_time) ? 1 : -1;
        }

        if (a.paid_time && !b.paid_time) return -1;
        if (!a.paid_time && b.paid_time) return 1;

        if (a.canceled_time && !b.canceled_time) return -1;
        if (!a.canceled_time && b.canceled_time) return 1;

        return dateIsBefore(a.creation_time, b.creation_time) ? 1 : -1;
      },
    },
  ],
});

async function getInvoice(transaction_id: string) {
  state.btnIsLoading = !!transaction_id;

  try {
    const { invoice_url } = await organisationApi.getTransactionInvoice(route.params.companyId as string, transaction_id);
    if (invoice_url) {
      window.open(invoice_url, "_blank");
    } else throw "No invoice";
  } catch {
    toast.error(t("app.company.history.toasts.invoiceError"));
  } finally {
    state.btnIsLoading = false;
  }
}
</script>

<style lang="scss" scoped>
.card {
  flex: 1;
}
</style>
